import { useState, type ReactElement } from 'react';

export interface OptionFiltre {
  valeur: string;
  libelle: string;
}

/**
 * Filtre à choix multiple, avec recherche.
 *
 * Un `details` natif : il s'ouvre au clavier comme à la souris, sans code de
 * focus à maintenir, et reste dans la rangée des filtres de l'écran. Rien de
 * choisi veut dire « tous » — le filtre ne restreint qu'une fois une case
 * cochée.
 */
export function FiltreMultiple({
  titre,
  options,
  choisis,
  onChange,
  libelleRecherche,
  placeholder = 'Code, motif ou libellé',
}: {
  titre: string;
  options: readonly OptionFiltre[];
  choisis: readonly string[];
  onChange: (valeurs: string[]) => void;
  libelleRecherche: string;
  placeholder?: string;
}): ReactElement {
  const [recherche, setRecherche] = useState('');
  const cherche = recherche.trim().toLowerCase();
  const visibles = options.filter((o) => o.libelle.toLowerCase().includes(cherche));

  function basculer(valeur: string): void {
    onChange(
      choisis.includes(valeur) ? choisis.filter((v) => v !== valeur) : [...choisis, valeur],
    );
  }

  return (
    <details className="champ filtre-multiple">
      <summary>
        <span className="filtre-multiple__titre">{titre}</span>
        <span className="filtre-multiple__etat">
          {choisis.length === 0 ? 'Tous' : `${choisis.length} choisi(s)`}
        </span>
      </summary>
      <div className="filtre-multiple__panneau">
        <input
          type="search"
          aria-label={libelleRecherche}
          placeholder={placeholder}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
        {visibles.length === 0 ? (
          <p className="sous-titre">Aucun choix ne correspond à cette recherche.</p>
        ) : (
          <ul className="filtre-multiple__options">
            {visibles.map((o) => (
              <li key={o.valeur}>
                <label>
                  <input
                    type="checkbox"
                    checked={choisis.includes(o.valeur)}
                    onChange={() => basculer(o.valeur)}
                  />{' '}
                  {o.libelle}
                </label>
              </li>
            ))}
          </ul>
        )}
        {choisis.length > 0 && (
          <button type="button" className="bouton bouton--lien" onClick={() => onChange([])}>
            Tout effacer
          </button>
        )}
      </div>
    </details>
  );
}
